import { useState, useEffect } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useLocation } from "wouter";
import { ChevronUp, ChevronDown, ChevronRight, Download, MoreHorizontal, Calendar } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { OrderSlip } from "@/components/order-slip";
import { ProcurementDialog } from "@/components/procurement-dialog";
import { useToast } from "@/hooks/use-toast";
import { apiRequest } from "@/lib/queryClient";
import { refreshProcurementPage } from "@/lib/procurementRefresh";
import * as React from "react";

import type { OrderWithDetails, Product, Category, OrderWindowConfig } from "@shared/schema";
import { CategoryIcon } from "@/components/category-icon";
import { UnitPill } from "@/components/unit-pill";

export default function Products() {
  const [fulfillmentDateFilter, setFulfillmentDateFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState(""); // Empty string means show all
  
  const [selectedOrder, setSelectedOrder] = useState<OrderWithDetails | null>(null);
  const [selectedProductId, setSelectedProductId] = useState<number | null>(null);
  const [isOrderSlipOpen, setIsOrderSlipOpen] = useState(false);
  const [sortConfig, setSortConfig] = useState<{key: string, direction: 'asc' | 'desc'}>({ key: 'totalQuantity', direction: 'desc' });
  const [openCategories, setOpenCategories] = useState<Set<number>>(new Set()); // All categories collapsed by default
  const { toast } = useToast();
  const queryClient = useQueryClient();
  
  // Procurement dialog state
  const [procurementDialog, setProcurementDialog] = useState<{
    isOpen: boolean;
    orderId?: number;
    itemId?: number;
    productId?: number;
    productName?: string;
    currentProcured?: number;
    totalOrdered?: number;
    isFullyProcured?: boolean;
  }>({ isOpen: false });

  // Lightweight orders for building date filter tabs — no items needed
  const { data: dateOrders = [] } = useQuery<any[]>({
    queryKey: ["/api/orders", { includeItems: false, limit: 200 }],
    queryFn: async () => {
      const res = await apiRequest("GET", "/api/orders?includeItems=false&limit=200");
      return res.json();
    },
    refetchInterval: 30000,
  });

  // Orders filtered by selected date — with items for aggregation and order slips
  const { data: orders = [], isLoading: ordersLoading } = useQuery<OrderWithDetails[]>({
    queryKey: ["/api/orders", { fulfillmentDate: fulfillmentDateFilter || undefined }],
    queryFn: async () => {
      const params = new URLSearchParams({ limit: "50" });
      if (fulfillmentDateFilter === "unscheduled") {
        params.set("fulfillmentDate", "unscheduled");
      } else if (fulfillmentDateFilter) {
        params.set("fulfillmentDate", fulfillmentDateFilter);
      }
      const res = await apiRequest("GET", `/api/orders?${params}`);
      return res.json();
    },
    refetchInterval: 30000, // Auto-refresh every 30 seconds
  });

  // Fetch products
  const { data: products = [], isLoading: productsLoading } = useQuery<Product[]>({
    queryKey: ["/api/products"],
  });

  // Aggregate procured (product_procurement) for the selected window — SOURCE OF TRUTH.
  // Keyed by productId; overlaid onto the per-product totals below (replaces the old
  // sum of per-line procuredQuantity, which is now decoupled/vestigial).
  const { data: procurementMap = {} } = useQuery<Record<number, { procuredQuantity: number; isFullyProcured: boolean }>>({
    queryKey: ["/api/procurement", { fulfillmentDate: fulfillmentDateFilter || undefined }],
    queryFn: async () => {
      const w = fulfillmentDateFilter || "all";
      const res = await apiRequest("GET", `/api/procurement?fulfillmentDate=${encodeURIComponent(w)}`);
      return res.json();
    },
    refetchInterval: 30000,
  });

  // Fetch categories
  const { data: categories = [], isLoading: categoriesLoading } = useQuery<Category[]>({
    queryKey: ["/api/categories"],
  });

  const { data: windowConfig } = useQuery<OrderWindowConfig>({
    queryKey: ["/api/order-window-config"],
  });

  const availableFulfillmentDates = React.useMemo(() => {
    const getFulfillmentDate = (order: any) => order.fulfillmentDate || order.pickupDate;

    const dateMap = new Map<string, number>();
    let unscheduledCount = 0;
    dateOrders.filter((o: any) => o.status !== 'cancelled').forEach((order: any) => {
      const date = getFulfillmentDate(order);
      if (date) {
        dateMap.set(date, (dateMap.get(date) || 0) + 1);
      } else {
        unscheduledCount++;
      }
    });

    const tz = (windowConfig as any)?.timezone || 'America/Toronto';
    const now = new Date(new Date().toLocaleString('en-US', { timeZone: tz }));
    const todayStart = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const dayNames = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
    const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

    const sortedDates = Array.from(dateMap.entries())
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([dateStr, count]) => {
        const [year, month, day] = dateStr.split('-').map(Number);
        const date = new Date(year, month - 1, day);
        const diffDays = Math.round((date.getTime() - todayStart.getTime()) / (1000 * 60 * 60 * 24));
        let label: string;
        if (diffDays === 0) label = `Today, ${monthNames[date.getMonth()]} ${date.getDate()}`;
        else if (diffDays === 1) label = `Tomorrow, ${monthNames[date.getMonth()]} ${date.getDate()}`;
        else label = `${dayNames[date.getDay()]}, ${monthNames[date.getMonth()]} ${date.getDate()}`;
        return { value: dateStr, label, count };
      });

    return { dates: sortedDates, unscheduledCount };
  }, [dateOrders, windowConfig]);

  React.useEffect(() => {
    const firstDate = availableFulfillmentDates.dates.length > 0
      ? availableFulfillmentDates.dates[0].value
      : availableFulfillmentDates.unscheduledCount > 0
        ? "unscheduled"
        : "";
    if (!fulfillmentDateFilter) {
      if (firstDate) setFulfillmentDateFilter(firstDate);
      return;
    }
    if (fulfillmentDateFilter === "unscheduled") {
      if (availableFulfillmentDates.unscheduledCount === 0) {
        setFulfillmentDateFilter(firstDate);
      }
      return;
    }
    const dateExists = availableFulfillmentDates.dates.some(d => d.value === fulfillmentDateFilter);
    if (!dateExists) setFulfillmentDateFilter(firstDate);
  }, [availableFulfillmentDates, fulfillmentDateFilter]);

  // Toggle category function
  const toggleCategory = (categoryId: number) => {
    setOpenCategories(prev => {
      const newSet = new Set(prev);
      if (newSet.has(categoryId)) {
        newSet.delete(categoryId);
      } else {
        newSet.add(categoryId);
      }
      return newSet;
    });
  };

  // Expand/Collapse all categories
  const expandAllCategories = () => {
    const allCategoryIds = new Set(categories.map(cat => cat.id));
    setOpenCategories(allCategoryIds);
  };

  const collapseAllCategories = () => {
    setOpenCategories(new Set());
  };

  // Handle order click with optional product highlighting
  const handleOrderClick = (order: OrderWithDetails, highlightProductId?: number) => {
    setSelectedOrder(order);
    setSelectedProductId(highlightProductId || null);
    setIsOrderSlipOpen(true);
  };

  // Handle order click by ID with optional product highlighting
  const handleOrderClickById = (orderId: number, highlightProductId?: number) => {
    const order = orders.find(o => o.id === orderId);
    if (order) {
      handleOrderClick(order, highlightProductId);
    }
  };

  // Handle procurement dialog
  const handleOpenProcurementDialog = (orderId: number, itemId: number, productId: number, productName: string, currentProcured: number, totalOrdered: number, isFullyProcured: boolean = false) => {
    // Procurement is tracked per fulfillment date. The "All dates" view (empty filter)
    // has no single window to write to — steer the user to pick a delivery day.
    if (!fulfillmentDateFilter) {
      toast({ title: "Pick a delivery date", description: "Procurement is tracked per delivery date — choose a date from the filter to update it." });
      return;
    }
    setProcurementDialog({
      isOpen: true,
      orderId,
      itemId,
      productId,
      productName,
      currentProcured,
      totalOrdered,
      isFullyProcured
    });
  };

  const handleCloseProcurementDialog = () => {
    setProcurementDialog({ isOpen: false });
  };

  // Handle Excel export
  const handleGenerateExcel = async () => {
    try {
      console.log('Exporting items to Excel');
      
      const response = await apiRequest("POST", "/api/orders/export-excel", {
        period: fulfillmentDateFilter || "all"
      });
      
      if (!response.ok) {
        throw new Error('Failed to generate Excel file');
      }

      const blob = await response.blob();
      
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      const dateSuffix = fulfillmentDateFilter ? `-${fulfillmentDateFilter}` : "";
      a.download = `items-export${dateSuffix}-${new Date().toISOString().split('T')[0]}.xlsx`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);

      toast({
        title: "Excel file generated",
        description: "Your items export has been downloaded successfully.",
      });
    } catch (error) {
      toast({
        title: "Export failed",
        description: "Failed to generate Excel file. Please try again.",
        variant: "destructive",
      });
    }
  };



  // Item-status classifier — mirrors the same predicates used by getProcurementStatusColor / getBadgeStatusStyling.
  // Used by the category rollup helper below; do not call from row rendering yet (the four inline predicates
  // elsewhere in this file are intentionally left untouched in this commit).
  type ItemStatus = 'pending' | 'short' | 'booked' | 'procured' | 'empty' | 'default';
  const getItemStatus = (product: any): ItemStatus => {
    const procured = product.totalProcured;
    const total = product.totalQuantity;
    const isFullyProcured = product.aggregatedFullyProcured;
    const isPending = product.anyPending;
    const anyProcurementModified = product.anyProcurementModified;

    if (isPending && procured === 0 && total > 0 && !anyProcurementModified) return 'pending';
    if (procured === 0 && total === 0) return 'empty';
    if ((procured > 0 && procured < total && total > 0) || (procured === 0 && total > 0 && anyProcurementModified)) return 'short';
    if (procured >= total && total > 0 && !isFullyProcured) return 'booked';
    if (procured >= total && total > 0 && isFullyProcured) return 'procured';
    return 'default';
  };

  // Category-level status rollup. Returns the shared status of every item in the category, or null when items
  // are empty / mixed / pending. Eligible rollup statuses: short, booked, procured. See PRODUCT.md §2
  // (Procurement-mobile protected zone) and DESIGN.md §5 (Procurement-mobile constraints).
  type CategoryRollupStatus = 'short' | 'booked' | 'procured' | null;
  const getCategoryRollupStatus = (items: any[]): CategoryRollupStatus => {
    if (items.length === 0) return null;
    const firstStatus = getItemStatus(items[0]);
    if (firstStatus !== 'short' && firstStatus !== 'booked' && firstStatus !== 'procured') return null;
    for (let i = 1; i < items.length; i++) {
      if (getItemStatus(items[i]) !== firstStatus) return null;
    }
    return firstStatus;
  };

  // Get procurement status color (now includes "booked" status and pending override) - Updated for modern design
  const getProcurementStatusColor = (procured: number, total: number, isFullyProcured: boolean = false, isPending: boolean = false, filterOverride: string | null = null, anyProcurementModified: boolean = false) => {
    // Always show true status colors, no filter overrides
    if (isPending && procured === 0 && total > 0 && !anyProcurementModified) return "border border-blue-500 bg-white hover:bg-blue-200"; // Pending items with thin blue outline and blue hover background
    if (procured === 0 && total === 0) return "border border-blue-500 bg-white"; // White with thin brand blue border for neutral (no orders)
    if ((procured > 0 && procured < total && total > 0) || (procured === 0 && total > 0 && anyProcurementModified)) return "border border-transparent bg-amber-100 hover:border-amber-400"; // Short items with amber outline on hover
    if (procured >= total && total > 0 && !isFullyProcured) return "border border-transparent bg-sky-50 hover:border-blue-500/50"; // Booked items (max quantity reached but not marked as procured)
    if (procured >= total && total > 0 && isFullyProcured) return "border border-transparent bg-green-100 hover:border-green-400"; // Fully procured items with green outline on hover
    return "border border-transparent bg-white"; // Clean fallback
  };

  // Get badge styling based on procurement status
  const getBadgeStatusStyling = (procured: number, total: number, isFullyProcured: boolean = false, isPending: boolean = false, filterOverride: string | null = null, anyProcurementModified: boolean = false) => {
    // Always show true status colors, no filter overrides
    if (isPending && procured === 0 && total > 0 && !anyProcurementModified) {
      return "bg-white border border-blue-400 text-blue-700 hover:bg-blue-700 hover:text-white hover:border-blue-700"; // Pending: white with blue border, inverted on hover
    }
    if ((procured > 0 && procured < total && total > 0) || (procured === 0 && total > 0 && anyProcurementModified)) {
      return "bg-white border border-amber-400 text-amber-700 hover:bg-amber-700 hover:text-white hover:border-amber-700"; // Short: white with amber border, inverted on hover
    }
    if (procured >= total && total > 0 && !isFullyProcured) {
      return "bg-white border border-blue-400 text-blue-700 hover:bg-blue-700 hover:text-white hover:border-blue-700"; // Booked: white with blue border, inverted on hover
    }
    if (procured >= total && total > 0 && isFullyProcured) {
      return "bg-white border border-green-400 text-green-700 hover:bg-green-700 hover:text-white hover:border-green-700"; // Procured: white with green border, inverted on hover
    }
    return "bg-white border border-gray-200 text-gray-700 hover:bg-gray-700 hover:text-white hover:border-gray-700"; // Default: neutral, inverted on hover
  };

  // Truncate customer name for popup display to prevent layout issues
  const truncateCustomerName = (name: string, maxLength: number = 20) => {
    if (name.length <= maxLength) return name;
    return name.substring(0, maxLength) + "...";
  };

  const handleSort = (key: string) => {
    let direction: 'asc' | 'desc' = 'asc';
    if (sortConfig && sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  const periodStats = React.useMemo(() => {
    const getFulfillmentDate = (order: any) => order.fulfillmentDate || order.pickupDate;
    let filteredOrders = orders;
    
    if (fulfillmentDateFilter) {
      if (fulfillmentDateFilter === "unscheduled") {
        filteredOrders = filteredOrders.filter(order => !getFulfillmentDate(order));
      } else {
        filteredOrders = filteredOrders.filter(order => getFulfillmentDate(order) === fulfillmentDateFilter);
      }
    }
    
    // Always exclude cancelled orders from stats
    filteredOrders = filteredOrders.filter(order => order.status !== 'cancelled');
    
    // Group products by name and aggregate quantities (same as productTotalsByCategory)
    const productTotalsMap = filteredOrders.reduce((acc: any, order) => {
      (order.items || []).filter(item => item && typeof item.quantity === 'number').forEach(item => {
        const productName = item.product.name;
        
        if (!acc[productName]) {
          acc[productName] = {
            productId: item.productId,
            totalQuantity: 0,
            totalProcured: 0,
            itemsWithQty: 0,
            itemsFullyProcured: 0,
            anyPending: false,
            pendingQuantity: 0,  // Track actual pending item quantities
            anyProcurementModified: false  // Track if any item has been modified
          };
        }
        
        acc[productName].totalQuantity += item.quantity;
        acc[productName].totalProcured += item.procuredQuantity || 0;
        
        // Count items with quantity > 0
        if (item.quantity > 0) {
          acc[productName].itemsWithQty += 1;
        }
        
        // Count items that are fully procured (only count items with quantity > 0)
        if (item.isFullyProcured && item.quantity > 0) {
          acc[productName].itemsFullyProcured += 1;
        }
        
        // Track pending items and their quantities
        if (item.isPending && item.quantity > 0) {
          acc[productName].anyPending = true;
          acc[productName].pendingQuantity += item.quantity;
        }
        
        // Track if any item has been manually modified
        if (item.procurementModified) {
          acc[productName].anyProcurementModified = true;
        }
      });
      return acc;
    }, {});
    
    // Overlay procured from the aggregate (product_procurement) — the source of truth.
    // totalProcured/aggregatedFullyProcured come from the per-window aggregate keyed by
    // productId; anyProcurementModified is reused as "touched" (an aggregate row exists).
    Object.keys(productTotalsMap).forEach(productName => {
      const product = productTotalsMap[productName];
      const agg = procurementMap[product.productId];
      product.totalProcured = agg?.procuredQuantity ?? 0;
      product.aggregatedFullyProcured = !!agg?.isFullyProcured;
      product.anyProcurementModified = !!agg;
    });

    // Calculate stats from aggregated product totals
    let totalItems = 0;
    let pendingItems = 0;
    let procuredItems = 0;
    let shortItems = 0;
    let bookedItems = 0;
    
    Object.values(productTotalsMap).forEach((product: any) => {
      const procured = product.totalProcured;
      const total = product.totalQuantity;
      const isFullyProcured = product.aggregatedFullyProcured;
      const pendingQty = product.pendingQuantity || 0;
      const isPending = product.anyPending;
      
      totalItems += 1; // Count each distinct product
      
      // Count items by status (1 item per status category - prioritize by procurement status)
      if (isFullyProcured) {
        procuredItems += 1; // Items manually marked as fully procured (highest priority)
      } else if (procured >= total && total > 0) {
        bookedItems += 1; // Items fully procured but not marked as complete
      } else if (procured > 0 && procured < total) {
        shortItems += 1; // Items with some procurement but still short
      } else if (procured === 0 && total > 0) {
        // Items with 0 procurement: check if they've been manually updated
        const hasBeenTouched = product.anyProcurementModified || false;
        if (hasBeenTouched) {
          shortItems += 1; // User manually set to 0 - remains short until reset
        } else if (isPending) {
          pendingItems += 1; // Never touched and has pending orders - truly pending
        }
      }
    });
    
    return {
      totalItems,
      pendingItems,
      procuredItems,
      shortItems,
      bookedItems
    };
  }, [orders, fulfillmentDateFilter, statusFilter, procurementMap]);

  const productTotalsByCategory = React.useMemo(() => {
    const getFulfillmentDate = (order: any) => order.fulfillmentDate || order.pickupDate;
    let filteredOrders = orders;
    
    if (fulfillmentDateFilter) {
      if (fulfillmentDateFilter === "unscheduled") {
        filteredOrders = filteredOrders.filter(order => !getFulfillmentDate(order));
      } else {
        filteredOrders = filteredOrders.filter(order => getFulfillmentDate(order) === fulfillmentDateFilter);
      }
    }
    
    // Always exclude cancelled orders from product totals
    filteredOrders = filteredOrders.filter(order => order.status !== 'cancelled');
    
    // Group products by category first, then apply procurement status filtering
    const productTotalsMap = filteredOrders.reduce((acc: any, order) => {
      (order.items || []).filter(item => item && typeof item.quantity === 'number').forEach(item => {
        const product = products.find(p => p.id === item.productId);
        if (!product) return;
        
        const categoryId = product.categoryId;
        const productName = item.product.name;
        
        if (!acc[categoryId]) {
          acc[categoryId] = {};
        }
        
        if (!acc[categoryId][productName]) {
          acc[categoryId][productName] = {
            productName,
            productId: item.productId,
            categoryId,
            totalQuantity: 0,
            totalProcured: 0,
            orderCount: 0,
            anyProcurementModified: false, // Track if any constituent item has been modified
            itemsWithQty: 0, // Count items with quantity > 0
            itemsFullyProcured: 0, // Count items that are fully procured
            anyPending: false, // Track if any item is manually marked as pending
            pendingQuantity: 0, // Track actual pending item quantities
            orderDetails: [] // Array of {orderId, quantity, procuredQuantity, itemId, customerName}
          };
        }
        
        acc[categoryId][productName].totalQuantity += item.quantity;
        acc[categoryId][productName].totalProcured += item.procuredQuantity || 0;
        
        // Track if any item has had procurement modified
        if (item.procurementModified) {
          acc[categoryId][productName].anyProcurementModified = true;
        }
        
        // Count items with quantity > 0
        if (item.quantity > 0) {
          acc[categoryId][productName].itemsWithQty += 1;
        }
        
        // Count items that are fully procured (only count items with quantity > 0)
        if (item.isFullyProcured && item.quantity > 0) {
          acc[categoryId][productName].itemsFullyProcured += 1;
        }
        
        // Track if any item is manually marked as pending (only for items with quantity > 0)
        if (item.isPending && item.quantity > 0) {
          acc[categoryId][productName].anyPending = true;
          acc[categoryId][productName].pendingQuantity += item.quantity;
        }
        
        // Track order details with quantities and procurement info
        const existingOrderDetail = acc[categoryId][productName].orderDetails.find((detail: any) => detail.orderId === order.id);
        if (existingOrderDetail) {
          existingOrderDetail.quantity += item.quantity;
          existingOrderDetail.procuredQuantity += item.procuredQuantity || 0;
        } else {
          acc[categoryId][productName].orderDetails.push({
            orderId: order.id,
            quantity: item.quantity,
            procuredQuantity: item.procuredQuantity || 0,
            itemId: item.id,
            customerName: order.account.name
          });
          acc[categoryId][productName].orderCount++;
        }
      });
      return acc;
    }, {});

    // Overlay procured from the aggregate (product_procurement) — the source of truth.
    Object.keys(productTotalsMap).forEach(categoryId => {
      Object.keys(productTotalsMap[categoryId]).forEach(productName => {
        const product = productTotalsMap[categoryId][productName];
        const agg = procurementMap[product.productId];
        product.totalProcured = agg?.procuredQuantity ?? 0;
        product.aggregatedFullyProcured = !!agg?.isFullyProcured;
        product.anyProcurementModified = !!agg;
      });
    });

    // Apply procurement status filtering (empty string means show all)
    if (statusFilter) {
      const filteredProductTotalsMap: any = {};
      
      Object.keys(productTotalsMap).forEach(categoryId => {
        const categoryProducts = productTotalsMap[categoryId];
        const filteredCategoryProducts: any = {};
        
        Object.keys(categoryProducts).forEach(productName => {
          const product = categoryProducts[productName];
          const procured = product.totalProcured;
          const total = product.totalQuantity;
          const isFullyProcured = product.aggregatedFullyProcured;
          const isPending = product.anyPending;
          
          let shouldInclude = false;
          
          if (statusFilter === "pending") {
            // Only items that are truly pending (never touched by user)
            const hasBeenTouched = product.anyProcurementModified || false;
            shouldInclude = isPending && procured === 0 && total > 0 && !hasBeenTouched;
          } else if (statusFilter === "short") {
            // Items with partial procurement OR manually zeroed by user
            const hasBeenTouched = product.anyProcurementModified || false;
            shouldInclude = (procured > 0 && procured < total && !isFullyProcured) || (procured === 0 && total > 0 && hasBeenTouched);
          } else if (statusFilter === "booked") {
            shouldInclude = procured >= total && total > 0 && !isFullyProcured; // Items fully procured but not marked as complete
          } else if (statusFilter === "procured") {
            shouldInclude = isFullyProcured; // Items manually marked as fully procured
          } else if (statusFilter === "") {
            shouldInclude = true; // Show all items when no filter is selected
          }
          
          if (shouldInclude) {
            filteredCategoryProducts[productName] = product;
          }
        });
        
        if (Object.keys(filteredCategoryProducts).length > 0) {
          filteredProductTotalsMap[categoryId] = filteredCategoryProducts;
        }
      });
      
      return filteredProductTotalsMap;
    }

    return productTotalsMap;
  }, [orders, products, fulfillmentDateFilter, statusFilter, procurementMap]);

  if (ordersLoading || productsLoading) {
    return (
      <div className="max-w-7xl mx-auto p-6">
        <div className="text-center">Loading products...</div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto p-4 sm:p-6 space-y-6">
      {/* Stats Cards */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        {[
          { key: 'pending', label: 'Pending', count: periodStats.pendingItems, color: 'text-blue-600', activeBg: 'bg-blue-50 border-blue-300', hoverBorder: 'hover:border-blue-200' },
          { key: 'short', label: 'Short', count: periodStats.shortItems, color: 'text-amber-500', activeBg: 'bg-amber-50 border-amber-300', hoverBorder: 'hover:border-amber-200' },
          { key: 'booked', label: 'Booked', count: periodStats.bookedItems, color: 'text-sky-500', activeBg: 'bg-sky-50 border-sky-300', hoverBorder: 'hover:border-sky-200' },
          { key: 'procured', label: 'Procured', count: periodStats.procuredItems, color: 'text-green-700', activeBg: 'bg-green-50 border-green-300', hoverBorder: 'hover:border-green-200' },
        ].map((s) => (
          <button
            key={s.key}
            onClick={() => setStatusFilter(statusFilter === s.key ? '' : s.key)}
            className={`rounded-lg border px-3 py-2.5 text-left transition-all ${
              statusFilter === s.key 
                ? s.activeBg 
                : `bg-white border-gray-200 ${s.hoverBorder}`
            }`}
          >
            <p className={`text-[10px] font-semibold uppercase tracking-wider ${s.color}`}>{s.label}</p>
            <p className={`text-xl font-bold ${s.color}`}>{s.count}</p>
          </button>
        ))}
      </div>
      {/* Product Totals Section */}
      <Card className="border border-gray-200 shadow-sm">
        <CardHeader className="border-b border-gray-100 bg-gray-50/50 py-3 px-4">
          <div className="flex items-center justify-between gap-3">
            <div className="flex items-center gap-2 flex-wrap">
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button
                    variant="outline"
                    size="sm"
                    className="h-8 w-8 p-0 bg-white hover:bg-gray-100 border border-gray-200"
                    data-testid="button-actions"
                  >
                    <MoreHorizontal className="h-4 w-4" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="start" className="w-48">
                  <DropdownMenuItem
                    onClick={handleGenerateExcel}
                    className="cursor-pointer"
                    data-testid="action-export-excel"
                  >
                    <Download className="mr-2 h-4 w-4" />
                    Excel Items
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>

              <div className="h-5 w-px bg-gray-200" />

              <Select value={fulfillmentDateFilter} onValueChange={setFulfillmentDateFilter}>
                <SelectTrigger className="w-[200px] h-8 text-xs bg-white border-gray-200">
                  <Calendar className="h-3.5 w-3.5 mr-1.5 text-gray-500" />
                  <SelectValue placeholder="Select Date" />
                </SelectTrigger>
                <SelectContent>
                  {availableFulfillmentDates.dates.map(({ value, label, count }) => (
                    <SelectItem key={value} value={value}>
                      {label} ({count})
                    </SelectItem>
                  ))}
                  {availableFulfillmentDates.unscheduledCount > 0 && (
                    <SelectItem value="unscheduled">
                      Unscheduled ({availableFulfillmentDates.unscheduledCount})
                    </SelectItem>
                  )}
                </SelectContent>
              </Select>
            </div>

            <div className="flex gap-1 flex-shrink-0">
              <Button
                variant="outline"
                size="sm"
                onClick={expandAllCategories}
                className="h-8 w-8 p-0 bg-white hover:bg-gray-100 border border-gray-200"
                data-testid="button-expand-all"
                title="Expand All"
              >
                <ChevronDown className="h-4 w-4" />
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={collapseAllCategories}
                className="h-8 w-8 p-0 bg-white hover:bg-gray-100 border border-gray-200"
                data-testid="button-collapse-all"
                title="Collapse All"
              >
                <ChevronUp className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent className="pt-4">
          {Object.keys(productTotalsByCategory).length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              No orders found
            </div>
          ) : (
            <div className="space-y-3">
              {categories
                .filter(category => {
                  const categoryProducts = productTotalsByCategory[category.id];
                  if (!categoryProducts) return false;
                  // Only show categories that have at least one product with quantity > 0
                  return Object.values(categoryProducts).some((product: any) => product.totalQuantity > 0);
                })
                .map(category => {
                  const categoryId = category.id;
                  const categoryProducts = productTotalsByCategory[categoryId] || {};
                  const productsArray = Object.values(categoryProducts);
                  // Only count products that have quantity > 0
                  const activeProducts = productsArray.filter((product: any) => product.totalQuantity > 0);
                  const categoryTotalQuantity = activeProducts.reduce((sum: number, product: any) => sum + product.totalQuantity, 0);
                  const categoryTotalOrders = activeProducts.reduce((sum: number, product: any) => sum + product.orderCount, 0);
                  const isOpen = openCategories.has(categoryId);
                  
                  // Category rollup: when every item in the category shares an eligible non-pending status,
                  // tint the header with the lighter sibling of that item color. Returns null for mixed /
                  // pending / empty categories (header stays neutral).
                  const categoryRollupStatus = getCategoryRollupStatus(activeProducts);

                  return (
                    <Collapsible
                      key={categoryId}
                      open={isOpen}
                      onOpenChange={() => toggleCategory(categoryId)}
                    >
                      <CollapsibleTrigger asChild>
                        <div className={`border rounded-lg p-4 transition-colors cursor-pointer backdrop-blur-sm ${
                          categoryRollupStatus === 'procured'
                            ? 'border-transparent bg-green-50 hover:bg-green-100'
                            : categoryRollupStatus === 'short'
                            ? 'border-transparent bg-amber-50 hover:bg-amber-100'
                            : categoryRollupStatus === 'booked'
                            ? 'border-transparent bg-sky-50 hover:bg-sky-100'
                            : 'border-gray-200 bg-white hover:bg-gray-50'
                        }`}>
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-3">
                              <CategoryIcon icon={category.icon} size="lg" />
                              <div>
                                <h3 className="font-semibold text-lg text-dark-grey">{category.name}</h3>
                                <p className="text-sm text-gray-600">
                                  {categoryTotalQuantity} items • {categoryTotalOrders} orders
                                </p>
                              </div>
                            </div>
                            <div className="flex items-center gap-2">
                              <div className="flex items-center">
                                <div className="text-2xl font-bold text-cn-brand-blue">{categoryTotalQuantity}</div>
                              </div>
                              <ChevronRight 
                                className={`h-5 w-5 text-gray-400 transition-transform ${isOpen ? 'rotate-90' : ''}`} 
                              />
                            </div>
                          </div>
                        </div>
                      </CollapsibleTrigger>
                      
                      <CollapsibleContent>
                        <div className="mt-2 space-y-2 pl-8 sm:pl-6">
                          {products
                            .filter(p => p.categoryId === categoryId && categoryProducts[p.name] && categoryProducts[p.name].totalQuantity > 0)
                            .map(p => ({ productData: categoryProducts[p.name], productInfo: p }))
                            .map(({ productData, productInfo }) => (
                            <div 
                              key={productData.productName} 
                              className={`rounded-lg p-3 mr-2 transition-colors ${getProcurementStatusColor(productData.totalProcured, productData.totalQuantity, productData.aggregatedFullyProcured, productData.anyPending, statusFilter, productData.anyProcurementModified)}`}
                              data-testid={`card-product-${productData.productId}`}
                            >
                              <div className="flex items-center justify-between">
                                <div className="flex-1">
                                  <button
                                    onClick={() => {
                                      // Prevent opening dialog for items with 0 quantity
                                      if (productData.totalQuantity === 0) {
                                        toast({
                                          title: "No Items to Procure",
                                          description: "This product has 0 quantity ordered",
                                          variant: "destructive",
                                        });
                                        return;
                                      }
                                      // Use aggregate data for total procurement management
                                      const firstOrderDetail = productData.orderDetails[0];
                                      if (firstOrderDetail) {
                                        handleOpenProcurementDialog(
                                          firstOrderDetail.orderId, 
                                          firstOrderDetail.itemId, 
                                          productData.productId, // Pass the product ID
                                          productData.productName, 
                                          productData.totalProcured, // Use aggregate total procured
                                          productData.totalQuantity, // Use aggregate total ordered
                                          productData.aggregatedFullyProcured // Pass fully procured status
                                        );
                                      }
                                    }}
                                    className="text-left w-full"
                                    data-testid={`button-product-${productData.productId}`}
                                  >
                                    <h4 className={`font-medium cursor-pointer ${
                                      (productData.anyPending && productData.totalProcured === 0 && !productData.anyProcurementModified) ? 'text-cn-brand-blue' :
                                      ((productData.totalProcured > 0 && productData.totalProcured < productData.totalQuantity) || (productData.totalProcured === 0 && productData.totalQuantity > 0 && productData.anyProcurementModified)) ? 'text-amber-700' :
                                      (productData.totalProcured >= productData.totalQuantity && !productData.aggregatedFullyProcured) ? 'text-[#38bdf9]' :
                                      (productData.totalProcured >= productData.totalQuantity && productData.aggregatedFullyProcured) ? 'text-cn-brand-green' :
                                      'text-sky-700'
                                    }`} data-testid={`text-product-name-${productData.productId}`}>
                                      {productData.productName}{productInfo.subheading ? ` - ${productInfo.subheading}` : ''}<UnitPill unit={productInfo.unit} />
                                    </h4>
                                    <div className="flex items-center gap-4 mt-1">
                                      <span className={`text-xs cursor-pointer underline ${
                                        (productData.anyPending && productData.totalProcured === 0 && !productData.anyProcurementModified) ? 'text-cn-brand-blue' :
                                        ((productData.totalProcured > 0 && productData.totalProcured < productData.totalQuantity) || (productData.totalProcured === 0 && productData.totalQuantity > 0 && productData.anyProcurementModified)) ? 'text-amber-700' :
                                        (productData.totalProcured >= productData.totalQuantity && !productData.aggregatedFullyProcured) ? 'text-[#38bdf9]' :
                                        (productData.totalProcured >= productData.totalQuantity && productData.aggregatedFullyProcured) ? 'text-cn-brand-green' :
                                        'text-sky-700'
                                      }`} data-testid={`text-units-${productData.productId}`}>
                                        {productData.totalProcured}/{productData.totalQuantity} units procured
                                      </span>
                                    </div>
                                  </button>
                                </div>
                                <Popover>
                                  <PopoverTrigger asChild>
                                    <div className="cursor-pointer">
                                      <Badge variant="secondary" className={`text-xs shadow-sm ${getBadgeStatusStyling(productData.totalProcured, productData.totalQuantity, productData.aggregatedFullyProcured, productData.anyPending, statusFilter, productData.anyProcurementModified)}`} data-testid={`badge-orders-${productData.productId}`}>
                                        {productData.orderCount} orders
                                      </Badge>
                                    </div>
                                  </PopoverTrigger>
                                  <PopoverContent 
                                    className="w-80 p-3"
                                    onPointerDownOutside={(e) => {
                                      e.stopPropagation();
                                    }}
                                    onInteractOutside={(e) => {
                                      e.stopPropagation();
                                    }}
                                  >
                                    <div className="space-y-2">
                                      <div className="space-y-1 max-h-48 overflow-y-auto">
                                        {productData.orderDetails.map((orderDetail: any) => (
                                          <div key={orderDetail.orderId} className="flex items-center justify-between p-2 hover:bg-gray-50 rounded">
                                            <Button
                                              variant="ghost"
                                              size="sm"
                                              onClick={() => handleOrderClickById(orderDetail.orderId, productData.productId)}
                                              className="flex-1 justify-start text-xs text-cn-brand-blue hover:text-cn-brand-blue-hover hover:bg-blue-50 p-1 h-auto"
                                              data-testid={`button-order-${orderDetail.orderId}`}
                                              title={`${orderDetail.customerName} #ORD-${orderDetail.orderId.toString().padStart(3, '0')}`}
                                            >
                                              {truncateCustomerName(orderDetail.customerName)} #ORD-{orderDetail.orderId.toString().padStart(3, '0')}
                                            </Button>
                                            <div className="flex flex-col items-end text-xs text-gray-600 ml-2">
                                              <span data-testid={`text-order-quantity-${orderDetail.orderId}`}>
                                                Qty: {orderDetail.quantity}
                                              </span>
                                            </div>
                                          </div>
                                        ))}
                                      </div>
                                    </div>
                                  </PopoverContent>
                                </Popover>
                              </div>
                            </div>
                          ))}
                        </div>
                      </CollapsibleContent>
                    </Collapsible>
                  );
                })}
            </div>
          )}
        </CardContent>
      </Card>
      {/* Order Slip Modal */}
      <OrderSlip
        order={selectedOrder}
        isOpen={isOrderSlipOpen}
        onClose={() => {
          setIsOrderSlipOpen(false);
          setSelectedProductId(null); // Clear highlight when closing
        }}
        highlightProductId={selectedProductId}
        onItemsChanged={() => refreshProcurementPage(queryClient)}
      />
      {/* Procurement Dialog */}
      <ProcurementDialog
        isOpen={procurementDialog.isOpen}
        onClose={handleCloseProcurementDialog}
        orderId={procurementDialog.orderId || 0}
        itemId={procurementDialog.itemId || 0}
        productId={procurementDialog.productId || 0}
        productName={procurementDialog.productName || ""}
        currentProcured={procurementDialog.currentProcured || 0}
        totalOrdered={procurementDialog.totalOrdered || 0}
        selectedPeriod={fulfillmentDateFilter}
        isFullyProcured={procurementDialog.isFullyProcured || false}
      />
    </div>
  );
}